'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { BarChart3, Building2 } from 'lucide-react';
import { useEffect } from 'react';
import { z } from 'zod';

import { PageHeader } from '@/components/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useGHGDashboard } from '@/features/ghg/api/use-ghg-dashboard';
import { useGHGOrganizationId } from '@/features/ghg/api/use-ghg-organization-id';
import { EmissionInsightsDashboard } from '@/features/ghg/components/EmissionInsightsDashboard';
import { EmissionsScopeSelector } from '@/features/ghg/components/EmissionsScopeSelector';
import { IndustryComparisonPage } from '@/features/ghg/components/IndustryComparisonPage';
import { YoYComparisonDashboard } from '@/features/ghg/components/YoYComparisonDashboard';
import { useGHGPagePeriod } from '@/features/ghg/hooks/use-ghg-page-period';
import { GhgScope1CalculationsPage } from '@/features/ghg/pages/GhgScope1CalculationsPage';
import { GhgScope2CalculationsPage } from '@/features/ghg/pages/GhgScope2CalculationsPage';
import { GhgScope3CalculationsPage } from '@/features/ghg/pages/GhgScope3CalculationsPage';
import i18n from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

const emissionsSearchSchema = z.object({
  tab: z.enum(['by-scope', 'by-facility', 'trends']).default('by-scope').catch('by-scope'),
  scope: z.coerce
    .number()
    .pipe(z.union([z.literal(1), z.literal(2), z.literal(3)]))
    .default(1)
    .catch(1),
  scope3Tab: z.enum(['workspace', 'sold-product-ghg']).default('workspace').catch('workspace'),
  category: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/ghg/emissions')({
  validateSearch: emissionsSearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.emissions'),
  }),
  component: EmissionsPage,
});

function EmissionsPage() {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();
  const { tab, scope, scope3Tab } = Route.useSearch();
  const { isLoaded: isOrgLoaded } = useOrganization();
  const orgId = useGHGOrganizationId();
  const { period, periodId } = useGHGPagePeriod();
  const reportingYear = period
    ? new Date(period.startDate).getUTCFullYear()
    : new Date().getFullYear();
  const { data: dashboardData, isLoading: facilityLoading } = useGHGDashboard(
    { reportingPeriodId: periodId, reportingYear },
    tab === 'by-facility'
  );

  useEffect(() => {
    if (scope !== 3 || scope3Tab !== 'sold-product-ghg') return;
    void navigate({
      to: '/ghg/scope3/sold-products',
      replace: true,
    });
  }, [navigate, scope, scope3Tab]);

  const handleTabChange = (value: string) => {
    void navigate({
      to: '/ghg/emissions',
      search: { tab: value as 'by-scope' | 'by-facility' | 'trends', scope },
      replace: true,
    });
  };

  const handleScopeChange = (newScope: 1 | 2 | 3) => {
    void navigate({
      to: '/ghg/emissions',
      search: { tab: 'by-scope', scope: newScope },
      replace: true,
    });
  };

  if (!isOrgLoaded || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-7xl">
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <PageHeader
          title={t('emissionsPage.title')}
          description={t('emissionsPage.description')}
          module="ghg"
          icon={<BarChart3 className="h-6 w-6" />}
        />

        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsList>
            <TabsTrigger value="by-scope">{t('emissionsPage.tab_byScope')}</TabsTrigger>
            <TabsTrigger value="by-facility">{t('emissionsPage.tab_byFacility')}</TabsTrigger>
            <TabsTrigger value="trends">{t('emissionsPage.tab_trends')}</TabsTrigger>
          </TabsList>

          <TabsContent value="by-scope" className="space-y-6">
            <EmissionsScopeSelector value={scope} onChange={handleScopeChange} />

            {scope === 1 && <GhgScope1CalculationsPage />}
            {scope === 2 && <GhgScope2CalculationsPage />}
            {scope === 3 && <GhgScope3CalculationsPage />}
          </TabsContent>

          <TabsContent value="by-facility" className="space-y-6">
            {facilityLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-20 w-full" />
                ))}
              </div>
            ) : dashboardData?.facilityBreakdown && dashboardData.facilityBreakdown.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">{t('emissionsPage.emissionsByFacility')}</CardTitle>
                  <CardDescription>
                    {t('emissionsPage.facilityBreakdownDescription', { year: reportingYear })}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboardData.facilityBreakdown.map((facility) => (
                      <div
                        key={facility.facilityId}
                        className="flex items-center justify-between rounded-lg border p-4"
                      >
                        <div className="flex items-center gap-3">
                          <div className="rounded-lg bg-muted p-2">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div>
                            <p className="font-medium">{facility.facilityName}</p>
                            <p className="text-xs capitalize text-muted-foreground">
                              {facility.facilityType}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">{t('emissionsPage.scope1')}</p>
                            <p className="text-sm font-medium">
                              {facility.scope1.toLocaleString()} {t('emissionsPage.unit_tCO2e')}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">{t('emissionsPage.scope2')}</p>
                            <p className="text-sm font-medium">
                              {facility.scope2.toLocaleString()} {t('emissionsPage.unit_tCO2e')}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">{t('emissionsPage.total')}</p>
                            <p className="text-sm font-bold">
                              {facility.total.toLocaleString()} {t('emissionsPage.unit_tCO2e')}
                            </p>
                          </div>
                          <div className="w-16 text-right">
                            <div className="mb-1 h-2 w-full rounded-full bg-muted">
                              <div
                                className={cn(
                                  'h-2 rounded-full bg-primary',
                                  facility.percentage < 10 && 'min-w-[4px]'
                                )}
                                style={{ width: `${String(Math.min(facility.percentage, 100))}%` }}
                              />
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {facility.percentage.toFixed(1)}%
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="rounded-lg border p-8 text-center text-muted-foreground">
                <Building2 className="mx-auto mb-3 h-10 w-10" />
                <h3 className="text-lg font-medium text-foreground">{t('emissionsPage.noFacilityData')}</h3>
                <p className="mt-1 text-sm">
                  {t('emissionsPage.noFacilityDataDescription', { year: reportingYear })}
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="trends" className="space-y-8">
            {/*
              TODO(WP4-B): Render <GroupScopePill /> at the top of the trends
              tab once `YoYComparisonDashboard` / `EmissionInsightsDashboard`
              surface the rollup scope metadata returned by the backend
              (`YoYComparisonResponse` and `EmissionInsightResponse` already
              inherit `RollupScopeMixin`). Skipped today because those
              components do not yet propagate the scope fields to the page
              level. See `docs/subsidiary-refactor/MIGRATION-GUIDE.md`.
            */}
            <section className="space-y-4">
              <h3 className="text-lg font-semibold">{t('emissionsPage.yoyComparison')}</h3>
              <YoYComparisonDashboard />
            </section>

            <section className="space-y-4">
              <h3 className="text-lg font-semibold">{t('emissionsPage.aiPoweredInsights')}</h3>
              <EmissionInsightsDashboard reportingPeriodId={periodId} />
            </section>

            <section className="space-y-4">
              <h3 className="text-lg font-semibold">{t('emissionsPage.industryBenchmarks')}</h3>
              <IndustryComparisonPage />
            </section>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
